import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";
import { ScrollableVirtualList, ScrollableVirtualListHandle } from "client/Components";
import { SelectableItemOptions, useUserTimezone } from "client/Hooks";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useRef
} from "react";
import { WebDavDocumentAction, WebDavDocumentActionContext } from "../../WebDav/actions";
import { AdditionalInformation } from "../../WebDav/WebDavListView";
import { GridItem } from "../../WebDav/WebDavOverview";
import { ItemPickerListItem } from "../ItemPickerListItem";
import { checkIsDocumentDisabled } from "./WebDavPickerOverview";
import { TimeZone } from "client/DateAndTime/timezones";
import { ItemPickerMode } from "../ItemPicker";
import { DocumentNoResult } from "../../DocumentNoResult";

export type WebDavPickerListViewHandle = {
	resetScrollPosition: () => void;
};

export type WebDavPickerListViewProps = {
	isBusy: boolean;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	isInSelectMode: boolean;
	items: readonly GridItem[];
	onOpenItem: (item: GridItem) => void;
	mode: ItemPickerMode;
	getAvailableOpenActions: (item: FileSystemItemModel) => readonly WebDavDocumentAction[];
	actionContext: WebDavDocumentActionContext;
	hasSearch: boolean;
};

export const WebDavPickerListView = forwardRef(
	(props: WebDavPickerListViewProps, ref: Ref<WebDavPickerListViewHandle>): ReactElement => {
		const {
			isBusy,
			items,
			onOpenItem,
			selectionFor,
			isInSelectMode,
			mode,
			getAvailableOpenActions,
			actionContext,
			hasSearch
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const listHandle: RefObject<ScrollableVirtualListHandle<GridItem> | null> = useRef<ScrollableVirtualListHandle<GridItem>>(null);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					listHandle.current
						?.scrollToTop();
				}
			}),
			[]
		);

		if (!isBusy && items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<ScrollableVirtualList
				items={items}
				estimatedHeightOfAnItem={64}
				itemKey={getUniqueKey}
				ref={listHandle}
				renderAfterItems={
					<ScrollableVirtualList.LoadMoreIndicator
						isLoadingData={isBusy}
						hasMoreItems={false}
						onRequestMoreItems={() => { /* NOOP */}}
					/>
				}
				trackingId="web-dav-picker-list-view"
			>
				{(item: GridItem): ReactElement => (
					<ItemPickerListItem
						name={item.name}
						documentKind={item.kind}
						isInSelectMode={isInSelectMode}
						onOpenItem={() => onOpenItem(item)}
						additionalInformation={<AdditionalInformation item={item} timezone={userTimezone} />}
						isBookmarked={item.hasBookmark}
						selectable={selectionFor(item)}
						isDisabled={checkIsDocumentDisabled(item, mode)}
						actionContext={{ ...actionContext, selectedDocuments: [item.model] }}
						actions={getAvailableOpenActions(item.model)}
					/>
				)}
			</ScrollableVirtualList>
		);
	}
);
WebDavPickerListView.displayName = "WebDavPickerListView";

function getUniqueKey(item: GridItem): string {
	return item.id;
}
